import { useNavigate } from "react-router";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";

import { Logo } from "@/components/common/logo";
import { MotionPage } from "@/components/common/motion";
import { useExistingDoctor } from "@/components/doctor/doctor-form";
import { EmailVerificationNotice } from "@/components/doctor/onboarding/email-verification-notice";
import { OnboardingWizard } from "@/components/doctor/onboarding/onboarding-wizard";
import { ShellSignOutButton } from "@/components/layout/app-shell";
import { ApiError } from "@/lib/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { usePageMeta } from "@/lib/page-meta";

export function RegisterRoute() {
  usePageMeta({
    title: "Doctor registration — HealNova",
    description:
      "Complete your HealNova clinician registration: specialization, qualifications, fees and weekly availability.",
  });
  return <RegisterPage />;
}

function RegisterPage() {
  const { uid, loading } = useAuth();
  const navigate = useNavigate();
  const existing = useExistingDoctor(uid);

  useEffect(() => {
    if (!loading && !uid) navigate("/login", { replace: true });
  }, [loading, uid, navigate]);

  // Already registered — the portal (or the review screen) is the right place.
  useEffect(() => {
    if (existing.data) navigate("/dashboard", { replace: true });
  }, [existing.data, navigate]);

  // The backend's doctorAuthMiddleware returns 403 when no Doctor row exists for the UID.
  const notRegistered =
    existing.error instanceof ApiError &&
    (existing.error.status === 404 || existing.error.status === 403);
  const checking = loading || !uid || (existing.isPending && !notRegistered);

  if (checking || existing.data) {
    return (
      <div className="grid min-h-screen place-items-center bg-background">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <div className="aurora-soft" aria-hidden="true" />

      <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-6">
        <Logo />
        <ShellSignOutButton />
      </header>

      <MotionPage as="main" className="relative z-10 mx-auto w-full max-w-6xl px-5 pb-20">
        <p className="text-xs font-semibold tracking-[0.2em] text-rose uppercase">
          Build your profile
        </p>
        <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight text-balance sm:text-4xl">
          Complete your practice registration
        </h1>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
          Our verification team reviews every clinician before their profile becomes visible to
          patients. You can refine any of this later from your profile.
        </p>

        <div className="mt-6 max-w-3xl">
          <EmailVerificationNotice />
        </div>

        <div className="mt-9">
          <OnboardingWizard onSuccess={() => navigate("/dashboard")} />
        </div>
      </MotionPage>
    </div>
  );
}
